import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { Header } from "../components/Layout";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Badge } from "../components/ui/badge";
import { Switch } from "../components/ui/switch";
import { Card, CardContent } from "../components/ui/card";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from "../components/ui/dialog";
import { Textarea } from "../components/ui/textarea";
import { Label } from "../components/ui/label";
import { toast } from "sonner";
import axios from "axios";
import {
  Plus, Loader2, Trash2, Edit, Zap, Mail, CheckSquare, ArrowRight,
  Clock, Copy, Search, Pencil, MessageSquare, Settings, ChevronDown, ChevronUp, Send,
  Map as MapIcon
} from "lucide-react";
import AutomationsMap from "../components/AutomationsMap";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

const SERVICE_SCOPES = [
  { value: "all", label: "All Services", color: "#6366f1" },
  { value: "roofing", label: "Roofing", color: "#0F4C81" },
  { value: "fencing", label: "Fencing", color: "#059669" },
  { value: "landscaping", label: "Landscaping", color: "#d97706" },
  { value: "water", label: "Water Systems", color: "#0ea5e9" },
];

const STYLE_LABELS = { system: "System", human: "Personal" };

const EVENT_LABELS = {
  quote_sent: "Quote Sent", quote_signed: "Quote Signed",
  payment_received: "Payment Received", invoice_created: "Invoice Created",
  task_completed: "Task Completed", lead_created: "Lead Created",
  appointment_set: "Appointment Set", appointment_completed: "Appointment Completed",
  appointment_cancelled: "Appointment Cancelled",
};
const TEMPLATE_TYPE_LABELS = {
  quote_followup: "Quote Follow-Up", appointment: "Appointment", billing: "Billing",
  review: "Review Request", referral: "Referral", stage_update: "Stage Update", custom: "Custom",
};

export default function Automations() {
  const navigate = useNavigate();
  const [workflows, setWorkflows] = useState([]);
  const [emailTemplates, setEmailTemplates] = useState([]);
  const [pipelineStages, setPipelineStages] = useState([]);
  const [pipelineCategories, setPipelineCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [activeSection, setActiveSection] = useState("cheatsheet");
  const [expandedWf, setExpandedWf] = useState(null);

  // Email template modal
  const [showTemplateModal, setShowTemplateModal] = useState(false);
  const [editingTemplate, setEditingTemplate] = useState(null);
  const [templateForm, setTemplateForm] = useState({ name: "", subject: "", body: "", style: "system", category: "shared", template_type: "custom" });

  const token = localStorage.getItem("token");
  const headers = { Authorization: `Bearer ${token}` };

  const fetchAll = useCallback(async () => {
    try {
      const [wfRes, etRes, stgRes, catRes] = await Promise.all([
        axios.get(`${API}/workflows`, { headers }),
        axios.get(`${API}/email-templates`, { headers }),
        axios.get(`${API}/pipeline-stages`, { headers }),
        axios.get(`${API}/pipeline-categories`, { headers }),
      ]);
      setWorkflows(wfRes.data);
      setEmailTemplates(etRes.data);
      setPipelineStages(stgRes.data);
      setPipelineCategories(catRes.data);
    } catch (e) { console.error(e); }
    setLoading(false);
  }, []);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const getStageName = (val) => pipelineStages.find(s => s.value === val)?.name || val?.replace(/_/g, " ") || "";
  const getScopeInfo = (val) => SERVICE_SCOPES.find(s => s.value === val) || SERVICE_SCOPES[0];

  const handleToggle = async (wf) => {
    try {
      await axios.patch(`${API}/workflows/${wf.id}/toggle`, {}, { headers });
      fetchAll();
      toast.success(wf.enabled ? "Workflow paused" : "Workflow activated");
    } catch { toast.error("Failed to toggle"); }
  };

  const handleDuplicate = async (wf) => {
    try {
      await axios.post(`${API}/workflows/${wf.id}/duplicate`, {}, { headers });
      fetchAll();
      toast.success("Workflow duplicated");
    } catch { toast.error("Failed to duplicate"); }
  };

  const handleDelete = async (wf) => {
    if (!window.confirm(`Delete "${wf.name}"?`)) return;
    try {
      await axios.delete(`${API}/workflows/${wf.id}`, { headers });
      fetchAll();
      toast.success("Workflow deleted");
    } catch { toast.error("Failed to delete"); }
  };

  // Email template CRUD
  const handleSaveTemplate = async (e) => {
    e.preventDefault();
    try {
      if (editingTemplate) {
        await axios.put(`${API}/email-templates/${editingTemplate.id}`, templateForm, { headers });
        toast.success("Template updated");
      } else {
        await axios.post(`${API}/email-templates`, templateForm, { headers });
        toast.success("Template created");
      }
      fetchAll();
      setShowTemplateModal(false);
      setEditingTemplate(null);
    } catch { toast.error("Failed to save template"); }
  };

  const handleDeleteTemplate = async (id) => {
    if (!window.confirm("Delete this message template?")) return;
    try {
      await axios.delete(`${API}/email-templates/${id}`, { headers });
      fetchAll();
      toast.success("Template deleted");
    } catch { toast.error("Failed to delete"); }
  };

  const openEditTemplate = (t) => {
    setEditingTemplate(t);
    setTemplateForm({ name: t.name, subject: t.subject, body: t.body, style: t.style, category: t.category, template_type: t.template_type });
    setShowTemplateModal(true);
  };

  // Helpers
  const getWorkflowDuration = (steps) => {
    let totalDays = 0;
    for (const s of (steps || [])) {
      if (s.step_type === "delay") {
        const val = s.delay_value || 0;
        const unit = s.delay_unit || "days";
        if (unit === "hours") totalDays += val / 24;
        else if (unit === "weeks") totalDays += val * 7;
        else totalDays += val;
      }
    }
    return Math.round(totalDays);
  };

  const getActionSummary = (steps) => {
    let emails = 0, tasks = 0, stages = 0;
    for (const s of (steps || [])) {
      if (s.step_type === "send_email" || s.step_type === "send_internal_email") emails++;
      else if (s.step_type === "create_task") tasks++;
      else if (s.step_type === "change_stage") stages++;
    }
    const parts = [];
    if (emails) parts.push(`${emails} email${emails > 1 ? "s" : ""}`);
    if (tasks) parts.push(`${tasks} task${tasks > 1 ? "s" : ""}`);
    if (stages) parts.push(`${stages} stage move${stages > 1 ? "s" : ""}`);
    return parts.join(" · ");
  };

  const stopLabels = {
    stage_changed: "Stage changes", project_sold: "Sold",
    project_closed: "Closed", payment_complete: "Paid",
  };

  // Filtered workflows by type
  const filtered = workflows.filter(wf => {
    if (searchTerm && !wf.name.toLowerCase().includes(searchTerm.toLowerCase())) return false;
    const wfType = wf.workflow_type || "stage";
    return wfType === activeSection;
  });

  // Filtered templates
  const filteredTemplates = emailTemplates.filter(t => {
    if (searchTerm && !t.name.toLowerCase().includes(searchTerm.toLowerCase()) && !t.subject?.toLowerCase().includes(searchTerm.toLowerCase())) return false;
    return true;
  });

  if (loading) return (
    <div className="min-h-screen">
      <Header title="Automations" />
      <div className="flex items-center justify-center py-20"><Loader2 className="w-8 h-8 animate-spin text-[#0F4C81]" /></div>
    </div>
  );

  return (
    <div className="min-h-screen" data-testid="automations-page">
      <Header title="Automations" />
      <div className="p-4 sm:p-6 max-w-5xl">

        {/* Section Switcher */}
        <div className="flex items-center gap-2 mb-6 overflow-x-auto no-scrollbar">
          <button
            onClick={() => { setActiveSection("cheatsheet"); setSearchTerm(""); }}
            className={`flex items-center gap-2 px-4 py-2.5 rounded-xl text-sm font-semibold transition-all flex-shrink-0 ${activeSection === "cheatsheet" ? "bg-amber-500 text-white shadow-md" : "bg-slate-100 text-slate-500 hover:bg-slate-200"}`}
            data-testid="tab-cheatsheet"
          >
            <MapIcon className="w-4 h-4" />
            Cheat Sheet
          </button>
          <button
            onClick={() => { setActiveSection("stage"); setSearchTerm(""); }}
            className={`flex items-center gap-2 px-4 py-2.5 rounded-xl text-sm font-semibold transition-all flex-shrink-0 ${activeSection === "stage" ? "bg-[#0F4C81] text-white shadow-md" : "bg-slate-100 text-slate-500 hover:bg-slate-200"}`}
            data-testid="tab-stage"
          >
            <Settings className="w-4 h-4" />
            Stage Workflows
          </button>
          <button
            onClick={() => { setActiveSection("event"); setSearchTerm(""); }}
            className={`flex items-center gap-2 px-4 py-2.5 rounded-xl text-sm font-semibold transition-all flex-shrink-0 ${activeSection === "event" ? "bg-[#0F4C81] text-white shadow-md" : "bg-slate-100 text-slate-500 hover:bg-slate-200"}`}
            data-testid="tab-event"
          >
            <Zap className="w-4 h-4" />
            Event Workflows
          </button>
          <button
            onClick={() => { setActiveSection("messaging"); setSearchTerm(""); }}
            className={`flex items-center gap-2 px-4 py-2.5 rounded-xl text-sm font-semibold transition-all flex-shrink-0 ${activeSection === "messaging" ? "bg-[#0F4C81] text-white shadow-md" : "bg-slate-100 text-slate-500 hover:bg-slate-200"}`}
            data-testid="tab-messaging"
          >
            <MessageSquare className="w-4 h-4" />
            Messaging
          </button>
        </div>

        {/* ========== CHEAT SHEET ========== */}
        {activeSection === "cheatsheet" && <AutomationsMap />}

        {/* ========== STAGE & EVENT WORKFLOWS ========== */}
        {(activeSection === "stage" || activeSection === "event") && (
          <div>
            {/* Section Title */}
            <h2 className="text-lg font-bold text-slate-800 mb-1 flex items-center gap-2" data-testid="section-title">
              {activeSection === "stage"
                ? <><Settings className="w-5 h-5 text-[#0F4C81]" />Stage Workflows</>
                : <><Zap className="w-5 h-5 text-[#0F4C81]" />Event Workflows</>
              }
            </h2>
            <p className="text-xs text-slate-400 mb-4">
              {activeSection === "stage"
                ? "When a project moves HERE, do THIS. Auto-runs when projects move through your pipeline columns."
                : "When THIS HAPPENS, do THAT. Fires on specific actions like a quote being signed, payment received, or appointment completed."
              }
            </p>

            {/* Header bar */}
            <div className="flex items-center justify-between mb-4 gap-3">
              <div className="relative flex-1 max-w-sm">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-300" />
                <Input
                  placeholder={`Search ${activeSection} workflows...`}
                  value={searchTerm}
                  onChange={e => setSearchTerm(e.target.value)}
                  className="pl-9 h-9 bg-slate-50 border-slate-200"
                  data-testid="workflow-search"
                />
              </div>
              <Button
                className="bg-[#0F4C81] hover:bg-[#0D4270] h-9"
                onClick={() => navigate(`/automations/build/new?type=${activeSection}`)}
                data-testid="create-workflow-btn"
              >
                <Plus className="w-4 h-4 mr-1.5" />New {activeSection === "stage" ? "Stage" : "Event"} Workflow
              </Button>
            </div>

            {/* Workflow Cards */}
            {filtered.length > 0 ? (
              <div className="space-y-2">
                {filtered.map(wf => {
                  const scope = getScopeInfo(wf.service_scope);
                  const duration = getWorkflowDuration(wf.steps);
                  const actionSummary = getActionSummary(wf.steps);
                  const isExpanded = expandedWf === wf.id;

                  return (
                    <Card
                      key={wf.id}
                      className={`transition-all border-l-4 ${wf.enabled ? "border-l-emerald-400 hover:shadow-md" : "border-l-slate-200 opacity-60"}`}
                      data-testid={`workflow-card-${wf.id}`}
                    >
                      <CardContent className="p-4">
                        {/* Main Row - always visible */}
                        <div className="flex items-center gap-3">
                          <Switch
                            checked={wf.enabled}
                            onCheckedChange={() => handleToggle(wf)}
                            data-testid={`toggle-wf-${wf.id}`}
                          />

                          <div
                            className="flex-1 min-w-0 cursor-pointer"
                            onClick={() => setExpandedWf(isExpanded ? null : wf.id)}
                          >
                            <div className="flex items-center gap-2">
                              <h3 className="text-sm font-bold text-slate-800 truncate">{wf.name}</h3>
                              <Badge
                                className="text-[9px] px-1.5 py-0 flex-shrink-0"
                                style={{ backgroundColor: `${scope.color}12`, color: scope.color, border: `1px solid ${scope.color}25` }}
                              >
                                {scope.label}
                              </Badge>
                            </div>
                            <div className="flex items-center gap-3 mt-0.5 text-xs text-slate-400">
                              <span className="flex items-center gap-1">
                                <ArrowRight className="w-3 h-3" />
                                {(wf.workflow_type || "stage") === "event"
                                  ? (EVENT_LABELS[wf.trigger_event] || wf.trigger_event || "—")
                                  : getStageName(wf.trigger_stage)
                                }
                              </span>
                              {actionSummary && <span>{actionSummary}</span>}
                              {duration > 0 && <span>{duration}d span</span>}
                            </div>
                          </div>

                          <div className="flex items-center gap-1 flex-shrink-0">
                            <Button variant="ghost" size="sm" className="h-8 w-8 p-0" onClick={() => navigate(`/automations/build/${wf.id}`)} data-testid={`edit-wf-${wf.id}`}>
                              <Edit className="w-3.5 h-3.5 text-slate-400" />
                            </Button>
                            <Button variant="ghost" size="sm" className="h-8 w-8 p-0" onClick={() => handleDuplicate(wf)} data-testid={`dup-wf-${wf.id}`}>
                              <Copy className="w-3.5 h-3.5 text-slate-400" />
                            </Button>
                            <Button variant="ghost" size="sm" className="h-8 w-8 p-0 text-red-400 hover:text-red-600 hover:bg-red-50" onClick={() => handleDelete(wf)} data-testid={`del-wf-${wf.id}`}>
                              <Trash2 className="w-3.5 h-3.5" />
                            </Button>
                            <button
                              className="h-8 w-8 flex items-center justify-center text-slate-300 hover:text-slate-500"
                              onClick={() => setExpandedWf(isExpanded ? null : wf.id)}
                              data-testid={`expand-wf-${wf.id}`}
                            >
                              {isExpanded ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                            </button>
                          </div>
                        </div>

                        {/* Expanded Details */}
                        {isExpanded && (
                          <div className="mt-3 pt-3 border-t border-slate-100 space-y-2">
                            {wf.summary && (
                              <p className="text-xs text-slate-500 leading-relaxed">{wf.summary}</p>
                            )}

                            {/* Step list - clean vertical list */}
                            <div className="space-y-1">
                              {(wf.steps || []).filter(s => s.step_type !== "condition").map((s, i) => {
                                const isDelay = s.step_type === "delay";
                                const isEmail = s.step_type === "send_email" || s.step_type === "send_internal_email";
                                const isTask = s.step_type === "create_task";
                                const isStage = s.step_type === "change_stage";
                                return (
                                  <div key={i} className="flex items-center gap-2 text-xs">
                                    <span className="w-5 text-right text-slate-300 font-mono text-[10px]">{i + 1}</span>
                                    {isDelay && <Clock className="w-3.5 h-3.5 text-slate-400" />}
                                    {isEmail && <Mail className="w-3.5 h-3.5 text-blue-500" />}
                                    {isTask && <CheckSquare className="w-3.5 h-3.5 text-amber-500" />}
                                    {isStage && <ArrowRight className="w-3.5 h-3.5 text-purple-500" />}
                                    <span className={`${isDelay ? "text-slate-400 italic" : "text-slate-600"}`}>
                                      {isDelay
                                        ? `Wait ${s.delay_value} ${s.delay_unit || "days"}`
                                        : isEmail
                                        ? `${s.step_type === "send_internal_email" ? "Internal email" : "Email to customer"}: ${s.email_template_name || s.label || "—"}`
                                        : isTask
                                        ? `Create task: ${s.task_title || s.label || "—"}`
                                        : isStage
                                        ? `Move to: ${getStageName(s.target_stage) || s.label || "—"}`
                                        : s.label || s.step_type}
                                    </span>
                                  </div>
                                );
                              })}
                            </div>

                            {/* Stop conditions */}
                            {(wf.stop_conditions || []).length > 0 && (
                              <p className="text-[11px] text-red-400">
                                Stops when: {wf.stop_conditions.map(s => stopLabels[s] || s).join(", ")}
                              </p>
                            )}
                          </div>
                        )}
                      </CardContent>
                    </Card>
                  );
                })}
              </div>
            ) : (
              <div className="text-center py-16">
                <div className="w-14 h-14 bg-[#0F4C81]/10 rounded-2xl flex items-center justify-center mx-auto mb-3">
                  {activeSection === "event" ? <Zap className="w-7 h-7 text-[#0F4C81]" /> : <Settings className="w-7 h-7 text-[#0F4C81]" />}
                </div>
                <h3 className="text-base font-semibold text-slate-700 mb-1">
                  {searchTerm ? `No matching ${activeSection} workflows` : `No ${activeSection} workflows yet`}
                </h3>
                <p className="text-slate-400 mb-4 text-sm max-w-sm mx-auto">
                  {activeSection === "stage"
                    ? "When a project moves HERE, do THIS. Great for follow-up sequences tied to your sales process."
                    : "When THIS HAPPENS, do THAT. Instant reactions like sending a thank you email when a quote is signed."
                  }
                </p>
                <Button className="bg-[#0F4C81] hover:bg-[#0D4270]" onClick={() => navigate(`/automations/build/new?type=${activeSection}`)}>
                  <Plus className="w-4 h-4 mr-1.5" />Create {activeSection === "stage" ? "Stage" : "Event"} Workflow
                </Button>
              </div>
            )}
          </div>
        )}

        {/* ========== MESSAGING ========== */}
        {activeSection === "messaging" && (
          <div>
            {/* Section Title */}
            <h2 className="text-lg font-bold text-slate-800 mb-1 flex items-center gap-2" data-testid="messaging-title">
              <MessageSquare className="w-5 h-5 text-[#0F4C81]" />Messaging Templates
            </h2>
            <p className="text-xs text-slate-400 mb-4">
              The email content your automations send. Reusable templates with variables like customer name and address.
            </p>

            {/* Header bar */}
            <div className="flex items-center justify-between mb-4 gap-3">
              <div className="relative flex-1 max-w-sm">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-300" />
                <Input
                  placeholder="Search messages..."
                  value={searchTerm}
                  onChange={e => setSearchTerm(e.target.value)}
                  className="pl-9 h-9 bg-slate-50 border-slate-200"
                  data-testid="messaging-search"
                />
              </div>
              <Button
                className="bg-[#0F4C81] hover:bg-[#0D4270] h-9"
                onClick={() => { setEditingTemplate(null); setTemplateForm({ name: "", subject: "", body: "", style: "system", category: "shared", template_type: "custom" }); setShowTemplateModal(true); }}
                data-testid="create-template-btn"
              >
                <Plus className="w-4 h-4 mr-1.5" />New Message
              </Button>
            </div>

            {/* Message Template Cards */}
            {filteredTemplates.length > 0 ? (
              <div className="space-y-2">
                {filteredTemplates.map(t => {
                  const scope = getScopeInfo(t.category);
                  return (
                    <Card key={t.id} className="transition-all hover:shadow-sm border-l-4 border-l-blue-300" data-testid={`template-card-${t.id}`}>
                      <CardContent className="p-4">
                        <div className="flex items-center gap-3">
                          <div className="w-9 h-9 rounded-lg bg-blue-50 flex items-center justify-center flex-shrink-0">
                            <Send className="w-4 h-4 text-blue-500" />
                          </div>

                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2">
                              <h4 className="text-sm font-bold text-slate-800 truncate">{t.name}</h4>
                              <Badge className={`text-[9px] px-1.5 py-0 ${t.style === "human" ? "bg-amber-50 text-amber-600 border-amber-200" : "bg-blue-50 text-blue-600 border-blue-200"}`}>
                                {STYLE_LABELS[t.style] || t.style}
                              </Badge>
                              <Badge className="text-[9px] px-1.5 py-0 bg-slate-50 text-slate-500 border-slate-200">
                                {TEMPLATE_TYPE_LABELS[t.template_type] || t.template_type}
                              </Badge>
                            </div>
                            <p className="text-xs text-slate-400 truncate mt-0.5">
                              <span className="font-medium text-slate-500">Subject:</span> {t.subject}
                            </p>
                          </div>

                          <div className="flex items-center gap-1 flex-shrink-0">
                            <Button variant="ghost" size="sm" className="h-8 w-8 p-0" onClick={() => openEditTemplate(t)}>
                              <Pencil className="w-3.5 h-3.5 text-slate-400" />
                            </Button>
                            <Button variant="ghost" size="sm" className="h-8 w-8 p-0 text-red-400 hover:text-red-600 hover:bg-red-50" onClick={() => handleDeleteTemplate(t.id)}>
                              <Trash2 className="w-3.5 h-3.5" />
                            </Button>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  );
                })}
              </div>
            ) : (
              <div className="text-center py-16">
                <div className="w-14 h-14 bg-blue-50 rounded-2xl flex items-center justify-center mx-auto mb-3">
                  <MessageSquare className="w-7 h-7 text-[#0F4C81]" />
                </div>
                <h3 className="text-base font-semibold text-slate-700 mb-1">
                  {searchTerm ? "No matching messages" : "No message templates yet"}
                </h3>
                <p className="text-slate-400 mb-4 text-sm max-w-sm mx-auto">
                  Create reusable messages for your automations. Email now, text messaging coming soon.
                </p>
                <Button className="bg-[#0F4C81] hover:bg-[#0D4270]" onClick={() => { setEditingTemplate(null); setTemplateForm({ name: "", subject: "", body: "", style: "system", category: "shared", template_type: "custom" }); setShowTemplateModal(true); }}>
                  <Plus className="w-4 h-4 mr-1.5" />Create First Message
                </Button>
              </div>
            )}
          </div>
        )}
      </div>

      {/* MESSAGE TEMPLATE MODAL */}
      <Dialog open={showTemplateModal} onOpenChange={() => setShowTemplateModal(false)}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editingTemplate ? "Edit Message" : "New Message Template"}</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSaveTemplate} className="space-y-4 mt-2">
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label className="text-xs text-slate-500">Name *</Label>
                <Input value={templateForm.name} onChange={e => setTemplateForm({ ...templateForm, name: e.target.value })} placeholder="e.g., Roofing Follow-Up Day 1" required className="h-9" data-testid="template-name" />
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs text-slate-500">Type</Label>
                <Select value={templateForm.template_type} onValueChange={v => setTemplateForm({ ...templateForm, template_type: v })}>
                  <SelectTrigger className="h-9" data-testid="template-type"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {Object.entries(TEMPLATE_TYPE_LABELS).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label className="text-xs text-slate-500">Tone</Label>
                <Select value={templateForm.style} onValueChange={v => setTemplateForm({ ...templateForm, style: v })}>
                  <SelectTrigger className="h-9" data-testid="template-style"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="system">System (Clean & Direct)</SelectItem>
                    <SelectItem value="human">Personal (Warm & Relational)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs text-slate-500">Service</Label>
                <Select value={templateForm.category} onValueChange={v => setTemplateForm({ ...templateForm, category: v })}>
                  <SelectTrigger className="h-9" data-testid="template-category"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="shared">All Services</SelectItem>
                    {SERVICE_SCOPES.filter(s => s.value !== "all").map(s => <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs text-slate-500">Subject Line *</Label>
              <Input value={templateForm.subject} onChange={e => setTemplateForm({ ...templateForm, subject: e.target.value })} placeholder="e.g., Thanks for having us out, {{first_name}}" required className="h-9" data-testid="template-subject" />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs text-slate-500">Message Body *</Label>
              <Textarea value={templateForm.body} onChange={e => setTemplateForm({ ...templateForm, body: e.target.value })} placeholder="Write your message content..." rows={8} required data-testid="template-body" />
              <p className="text-[10px] text-slate-400">Variables: {"{{first_name}}"}, {"{{contact_name}}"}, {"{{project_address}}"}, {"{{company_name}}"}, {"{{rep_name}}"}</p>
            </div>
            <div className="flex gap-3 pt-2">
              <Button type="button" variant="outline" className="flex-1 h-10" onClick={() => setShowTemplateModal(false)}>Cancel</Button>
              <Button type="submit" className="flex-1 h-10 bg-[#0F4C81] hover:bg-[#0D4270]" data-testid="save-template-btn">
                {editingTemplate ? "Update" : "Create"} Message
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
